import {
  AlertCircle,
  Award,
  CheckCircle2,
  Coins,
  Download,
  Hammer,
  Heart,
  Layers,
  LucideIcon,
  MessageCircle,
  Newspaper,
  ShieldCheck,
  Sparkles,
  Star,
  Trophy,
  UserPlus,
} from 'lucide-react';
import {
  ActivityVerb,
  IActivityEntity,
  IActivityGroup,
  IActivityItem,
} from '@/redux/activity/activity.d';

/** A card is one verb, or several verbs on one thing. */
export type CardKind = ActivityVerb | 'MIXED';

export const VERB_ICON: Record<CardKind, LucideIcon> = {
  MADE: Hammer,
  COMMENTED: MessageCircle,
  LIKED: Heart,
  FOLLOWED: UserPlus,
  PUBLISHED: Sparkles,
  APPROVED: CheckCircle2,
  REJECTED: AlertCircle,
  FEATURED: Star,
  COINS_EARNED: Coins,
  TIER_UP: Trophy,
  BADGE: ShieldCheck,
  DOWNLOAD_MILESTONE: Download,
  BLOG_PUBLISHED: Newspaper,
  ACHIEVEMENT: Award,
  MIXED: Layers,
};

/** Small badge on an actor's avatar; follows carry none. */
export const AVATAR_BADGE_TONE: Partial<Record<CardKind, string>> = {
  MADE: 'bg-amber-500',
  COMMENTED: 'bg-sky-500',
  LIKED: 'bg-rose-500',
  PUBLISHED: 'bg-green-600',
  COINS_EARNED: 'bg-yellow-500',
};

/** Icon tile standing in for an avatar when nobody performed the event. */
export const SYSTEM_TONE: Partial<Record<CardKind, string>> = {
  APPROVED: 'bg-green-50 text-green-700',
  REJECTED: 'bg-amber-50 text-amber-700',
  FEATURED: 'bg-yellow-50 text-yellow-600',
  TIER_UP: 'bg-violet-50 text-violet-700',
  BADGE: 'bg-sky-50 text-sky-700',
  DOWNLOAD_MILESTONE: 'bg-green-50 text-green-700',
  BLOG_PUBLISHED: 'bg-slate-100 text-slate-700',
  ACHIEVEMENT: 'bg-amber-50 text-amber-700',
};

/** A sentence in parts, so the actor and title can be bold. */
export interface SentenceParts {
  actor: string | null;
  before: string;
  title: string | null;
  after: string;
}

const meta = (item: IActivityItem, key: string) => item.meta?.[key] ?? '';

const titleOf = (item: { entity: { title: string } | null }) =>
  item.entity?.title ?? null;

const capitalise = (word: string | number) =>
  String(word).charAt(0).toUpperCase() + String(word).slice(1);

const FEATURED_TEXT: Record<string, string> = {
  featured: ' is featured on the homepage.',
  fileOfTheWeek: ' is File of the Week.',
};

const CREATOR_OF_THE_MONTH = {
  before: 'You’re Creator of the Month.',
  title: null,
  after: '',
};

type PartsBuilder = (item: IActivityItem) => Omit<SentenceParts, 'actor'>;

const SENTENCE: Record<ActivityVerb, PartsBuilder> = {
  MADE: (item) => ({
    before: 'made',
    title: titleOf(item),
    after: '',
  }),
  COMMENTED: (item) => ({
    before: 'commented on',
    title: titleOf(item),
    after: item.excerpt ? `: ${item.excerpt}` : '',
  }),
  LIKED: (item) => ({
    before: 'liked',
    title: titleOf(item),
    after: '',
  }),
  FOLLOWED: () => ({
    before: 'started following you.',
    title: null,
    after: '',
  }),
  PUBLISHED: (item) => ({
    before: 'published',
    title: titleOf(item),
    after: '',
  }),
  APPROVED: (item) => ({
    before: '',
    title: titleOf(item),
    after: ' was approved and is now live.',
  }),
  REJECTED: (item) => ({
    before: '',
    title: titleOf(item),
    after: ' needs changes before it can go live.',
  }),
  FEATURED: (item) => {
    const kind = String(meta(item, 'kind'));
    if (kind === 'creatorOfTheMonth') return CREATOR_OF_THE_MONTH;
    return {
      before: '',
      title: titleOf(item),
      after: FEATURED_TEXT[kind] ?? ' was featured.',
    };
  },
  COINS_EARNED: (item) => ({
    before: 'made',
    title: titleOf(item),
    after: ` and earned you ${meta(item, 'points')} coins.`,
  }),
  TIER_UP: (item) => ({
    before: `You reached ${capitalise(meta(item, 'tier'))} tier.`,
    title: null,
    after: '',
  }),
  BADGE: () => ({
    before: 'You earned the Trusted Member badge.',
    title: null,
    after: '',
  }),
  DOWNLOAD_MILESTONE: (item) => ({
    before: '',
    title: titleOf(item),
    after: ` reached ${meta(item, 'count')} downloads.`,
  }),
  BLOG_PUBLISHED: (item) => ({
    before: 'New on the blog:',
    title: titleOf(item),
    after: '',
  }),
  ACHIEVEMENT: (item) => ({
    before: 'You unlocked',
    title: String(meta(item, 'name')),
    after: ` · +${meta(item, 'points')} XP`,
  }),
};

export function itemSentence(item: IActivityItem): SentenceParts {
  return {
    actor: item.actor ? `@${item.actor.username}` : null,
    ...SENTENCE[item.verb](item),
  };
}

const entityPath = (item: { entityType: string; entity: { _id: string } }) =>
  item.entityType === 'projectGroup'
    ? `/project-groups/${item.entity._id}`
    : `/model/${item.entity._id}`;

const editPath = (item: { entityType: string; entity: { _id: string } }) =>
  item.entityType === 'projectGroup'
    ? `/project/v2/group/${item.entity._id}/edit`
    : `/project/v2/edit/${item.entity._id}`;

// the detail pages scroll to #comment-<id> and open #make-<id>
const ANCHOR: Partial<Record<ActivityVerb, string>> = {
  COMMENTED: 'comment',
  MADE: 'make',
};

const anchoredPath = (item: IActivityItem, entity: IActivityEntity) => {
  const anchor = ANCHOR[item.verb];
  const base = entityPath({ entityType: item.entityType, entity });
  return anchor ? `${base}#${anchor}-${item.sourceId}` : base;
};

type PathRule = (item: IActivityItem, me: string) => string | null;

// first rule that answers wins
const PATH_RULES: PathRule[] = [
  (item) =>
    item.verb === 'FOLLOWED' && item.actor ? `/${item.actor.username}` : null,
  (item, me) => (item.entity ? null : `/${me}`),
  (item) =>
    item.entityType === 'blog' && item.entity
      ? `/blog/${item.entity.slug}`
      : null,
  (item) =>
    item.verb === 'REJECTED' && item.entity
      ? editPath({ entityType: item.entityType, entity: item.entity })
      : null,
  (item) => (item.entity ? anchoredPath(item, item.entity) : null),
];

/** Where a row leads: the exact comment or make, the edit page for a rejection, or your profile. */
export function itemPath(item: IActivityItem, myUsername: string): string {
  return (
    PATH_RULES.map((rule) => rule(item, myUsername)).find(Boolean) ??
    `/${myUsername}`
  );
}

export function groupPath(group: IActivityGroup): string | null {
  if (!group.entity) return null;
  return entityPath({ entityType: group.entityType, entity: group.entity });
}

const NOUN: Partial<Record<ActivityVerb, [string, string]>> = {
  COMMENTED: ['comment', 'comments'],
  LIKED: ['like', 'likes'],
  MADE: ['make', 'makes'],
};

const GROUP_VERB: Partial<Record<ActivityVerb, string>> = {
  COMMENTED: 'commented on',
  LIKED: 'liked',
  MADE: 'made',
  FOLLOWED: 'started following you.',
};

/** "@jane", "@jane and @raj", "@jane, @raj and 3 others" */
function actorNames(group: IActivityGroup): string {
  const names = group.actors.map((actor) => `@${actor.username}`);
  const others = group.actorCount - names.length;
  if (others > 0) {
    return `${names.join(', ')} and ${others} other${others > 1 ? 's' : ''}`;
  }
  if (names.length < 2) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

const breakdown = (group: IActivityGroup) =>
  (Object.keys(NOUN) as ActivityVerb[])
    .filter((verb) => group.verbCounts[verb])
    .map((verb) => {
      const count = group.verbCounts[verb] ?? 0;
      const [one, many] = NOUN[verb] ?? [verb, verb];
      return `${count} ${count === 1 ? one : many}`;
    })
    .join(', ');

const mixedSentence = (group: IActivityGroup): SentenceParts => ({
  actor: null,
  before: '',
  title: titleOf(group),
  after: `: ${breakdown(group)}`,
});

/** One verb: "@jane and 4 others liked X". Mixed: "X: 2 comments, 4 likes". */
export function groupSentence(group: IActivityGroup): SentenceParts {
  const verbs = Object.keys(group.verbCounts) as ActivityVerb[];
  if (verbs.length > 1) return mixedSentence(group);
  return {
    actor: actorNames(group),
    before: GROUP_VERB[verbs[0]] ?? '',
    title: titleOf(group),
    after: '',
  };
}

export function groupKind(group: IActivityGroup): CardKind {
  const verbs = Object.keys(group.verbCounts) as ActivityVerb[];
  return verbs.length === 1 ? verbs[0] : 'MIXED';
}
